import { useState, useRef, useEffect, forwardRef } from 'react';
import {
  Box,
  Input,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  VStack,
  HStack,
  Text,
  IconButton,
  useColorModeValue,
  useDisclosure,
  Spinner,
  Badge,
  Flex,
  Avatar,
  Icon,
  Button,
} from '@chakra-ui/react';
import { FiSearch, FiX, FiUsers, FiHome, FiFileText, FiList } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth, RoleLevel } from '@bemap/utils';
import { AdminResults } from './admin-results';
import { AnalystResults } from './analyst-results';
import { PartnerResults } from './partner-results';
import { IoSearch } from 'react-icons/io5';

export interface SearchResult {
  id: string;
  type: 'assessment' | 'person' | 'company' | 'group';
  title: string;
  subtitle?: string;
  description?: string;
  avatar?: string;
  href: string;
  metadata?: Record<string, any>;
}

export const GlobalSearch = forwardRef<HTMLDivElement, {}>((_, ref) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const modalBg = useColorModeValue('white', 'gray.800');
  const overlayBg = useColorModeValue('blackAlpha.600', 'blackAlpha.800');

  // Mock search function - replace with actual API call later
  const performSearch = async (searchQuery: string): Promise<SearchResult[]> => {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 300));

    if (!searchQuery.trim()) return [];

    const mockResults: SearchResult[] = [
      // Assessments
      {
        id: '1',
        type: 'assessment',
        title: 'Avaliação de Competências 2024',
        subtitle: 'Status: Em andamento',
        description: 'Avaliação de competências para equipe de desenvolvimento',
        href: '/avaliacoes/1',
        metadata: { status: 'in_progress', participants: 15 }
      },
      {
        id: '2',
        type: 'assessment',
        title: 'Teste de Personalidade',
        subtitle: 'Status: Concluído',
        description: 'Avaliação de perfil comportamental',
        href: '/avaliacoes/2',
        metadata: { status: 'completed', participants: 8 }
      },
      // People
      {
        id: '3',
        type: 'person',
        title: 'João Silva',
        subtitle: 'Analista de RH',
        description: 'joao.silva@empresa.com',
        avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face',
        href: '/pessoas/3',
        metadata: { department: 'RH', status: 'active' }
      },
      {
        id: '4',
        type: 'person',
        title: 'Maria Santos',
        subtitle: 'Gerente de Projetos',
        description: 'maria.santos@empresa.com',
        avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face',
        href: '/pessoas/4',
        metadata: { department: 'TI', status: 'active' }
      },
      // Companies
      {
        id: '5',
        type: 'company',
        title: 'TechCorp Solutions',
        subtitle: 'Tecnologia',
        description: 'Empresa de desenvolvimento de software',
        href: '/empresas/5',
        metadata: { employees: 150, industry: 'Technology' }
      },
      {
        id: '6',
        type: 'company',
        title: 'Inovação Digital Ltda',
        subtitle: 'Consultoria',
        description: 'Consultoria em transformação digital',
        href: '/empresas/6',
        metadata: { employees: 45, industry: 'Consulting' }
      },
      // Groups
      {
        id: '7',
        type: 'group',
        title: 'Equipe de Desenvolvimento',
        subtitle: '15 membros',
        description: 'Grupo de desenvolvedores da empresa',
        href: '/listas/7',
        metadata: { members: 15, type: 'development' }
      },
      {
        id: '8',
        type: 'group',
        title: 'Líderes de Projeto',
        subtitle: '8 membros',
        description: 'Grupo de líderes e gerentes',
        href: '/listas/8',
        metadata: { members: 8, type: 'leadership' }
      }
    ];

    // Filter results based on query
    return mockResults.filter(result =>
      result.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      result.subtitle?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      result.description?.toLowerCase().includes(searchQuery.toLowerCase())
    );
  };

  const handleSearch = async (searchQuery: string) => {
    setQuery(searchQuery);

    if (!searchQuery.trim()) {
      setResults([]);
      return;
    }

    setIsLoading(true);
    try {
      const searchResults = await performSearch(searchQuery);
      setResults(searchResults);
    } catch (error) {
      console.error('Search error:', error);
      setResults([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResultClick = (result: SearchResult) => {
    navigate(result.href);
    onClose();
    setQuery('');
  };

  const handleClear = () => {
    setQuery('');
    setResults([]);
    setSelectedIndex(0);
    inputRef.current?.focus();
  };

  // Keyboard shortcuts for Spotlight-like behavior
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Cmd/Ctrl + K to open search
      if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
        event.preventDefault();
        onOpen();
      }

      // Escape to close search
      if (event.key === 'Escape' && isOpen) {
        event.preventDefault();
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onOpen, onClose]);

  // Handle keyboard navigation in results
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (!isOpen) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setSelectedIndex(prev =>
          prev < results.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        event.preventDefault();
        setSelectedIndex(prev =>
          prev > 0 ? prev - 1 : results.length - 1
        );
        break;
      case 'Enter':
        event.preventDefault();
        if (results[selectedIndex]) {
          handleResultClick(results[selectedIndex]);
        }
        break;
      case 'Escape':
        event.preventDefault();
        onClose();
        break;
    }
  };

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    } else {
      setQuery('');
      setResults([]);
      setSelectedIndex(0);
    }
  }, [isOpen]);

  const getTypeIcon = (type: SearchResult['type']) => {
    switch (type) {
      case 'assessment':
        return <FiFileText />;
      case 'person':
        return <FiUsers />;
      case 'company':
        return <FiHome />;
      case 'group':
        return <FiList />;
      default:
        return <FiSearch />;
    }
  };

  const getTypeLabel = (type: SearchResult['type']) => {
    switch (type) {
      case 'assessment':
        return t('search.assessment');
      case 'person':
        return t('search.person');
      case 'company':
        return t('search.company');
      case 'group':
        return t('search.group');
      default:
        return type;
    }
  };

  const getTypeColor = (type: SearchResult['type']) => {
    switch (type) {
      case 'assessment':
        return 'blue';
      case 'person':
        return 'green';
      case 'company':
        return 'purple';
      case 'group':
        return 'orange';
      default:
        return 'gray';
    }
  };

  const renderResults = () => {
    if (isLoading) {
      return (
        <Flex justify="center" py={8}>
          <Spinner size="md" />
        </Flex>
      );
    }

    if (query && results.length === 0) {
      return (
        <Box py={8} textAlign="center">
          <Text color="gray.500">{t('search.no_results')}</Text>
        </Box>
      );
    }

    if (results.length === 0) {
      return (
        <Box py={8} textAlign="center">
          <Text color="gray.500">{t('search.start_typing')}</Text>
        </Box>
      );
    }

    // Render role-specific results with selection highlighting
    if (user?.permission === RoleLevel.ADMIN || user?.permission === RoleLevel.SISADM) {
      return <AdminResults results={results} onResultClick={handleResultClick} selectedIndex={selectedIndex} />;
    } else if (user?.permission === RoleLevel.PARTNER) {
      return <PartnerResults results={results} onResultClick={handleResultClick} selectedIndex={selectedIndex} />;
    } else if (user?.permission === RoleLevel.ANALYST) {
      return <AnalystResults results={results} onResultClick={handleResultClick} selectedIndex={selectedIndex} />;
    }

    // Fallback to grouped results for unknown roles
    const groupedResults = results.reduce((acc, result) => {
      if (!acc[result.type]) {
        acc[result.type] = [];
      }
      acc[result.type].push(result);
      return acc;
    }, {} as Record<string, SearchResult[]>);

    let globalIndex = 0;
    return (
      <VStack spacing={0} align="stretch">
        {Object.entries(groupedResults).map(([type, typeResults]) => (
          <Box key={type}>
            <Flex px={4} py={2} bg={useColorModeValue('gray.50', 'gray.700')} align="center">
              <Box color={`${getTypeColor(type as SearchResult['type'])}.500`} mr={2}>
                {getTypeIcon(type as SearchResult['type'])}
              </Box>
              <Text fontSize="sm" fontWeight="semibold" color="gray.600">
                {getTypeLabel(type as SearchResult['type'])} ({typeResults.length})
              </Text>
            </Flex>
            <VStack spacing={0} align="stretch">
              {typeResults.map((result, index) => {
                const isSelected = globalIndex === selectedIndex;
                globalIndex++;
                return (
                  <Box
                    key={result.id}
                    px={4}
                    py={3}
                    cursor="pointer"
                    bg={isSelected ? hoverBg : 'transparent'}
                    _hover={{ bg: hoverBg }}
                    onClick={() => handleResultClick(result)}
                    borderBottom={index < typeResults.length - 1 ? '1px solid' : 'none'}
                    borderColor={useColorModeValue('gray.100', 'gray.600')}
                    borderLeft={isSelected ? '3px solid' : '3px solid transparent'}
                    borderLeftColor="blue.500"
                  >
                    <HStack spacing={3} align="start">
                      {result.avatar ? (
                        <Avatar size="sm" src={result.avatar} name={result.title} />
                      ) : (
                        <Box
                          p={2}
                          borderRadius="md"
                          bg={`${getTypeColor(result.type)}.100`}
                          color={`${getTypeColor(result.type)}.600`}
                        >
                          {getTypeIcon(result.type)}
                        </Box>
                      )}
                      <Box flex={1}>
                        <Text fontWeight="medium" fontSize="sm" noOfLines={1}>
                          {result.title}
                        </Text>
                        {result.subtitle && (
                          <Text fontSize="xs" color="gray.500" noOfLines={1}>
                            {result.subtitle}
                          </Text>
                        )}
                        {result.description && (
                          <Text fontSize="xs" color="gray.400" noOfLines={1}>
                            {result.description}
                          </Text>
                        )}
                      </Box>
                      <Badge size="sm" colorScheme={getTypeColor(result.type)} variant="subtle">
                        {getTypeLabel(result.type)}
                      </Badge>
                    </HStack>
                  </Box>
                );
              })}
            </VStack>
          </Box>
        ))}
      </VStack>
    );
  };

  return (
    <Box ref={ref}>
      {/* Search trigger button */}
      <Button
        onClick={onOpen}
        variant='no-hover'
        bg='transparent'
        p='0px'
        minW='unset'
        minH='unset'
        h='18px'
        w='max-content'>
        <Icon
          mt='0px'
          me='10px'
          h='18px'
          w='18px'
          color={useColorModeValue('gray.400', 'gray.600')}
          as={IoSearch}
        />
      </Button>

      {/* Spotlight Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered>
        <ModalOverlay bg={overlayBg} backdropFilter="blur(4px)" />
        <ModalContent
          bg={modalBg}
          borderRadius="xl"
          boxShadow="2xl"
          border="1px solid"
          borderColor={useColorModeValue('gray.200', 'gray.600')}
          maxW="800px"
          maxH="600px"
          overflow="hidden"
        >
          <ModalBody p={0}>
            {/* Search Input */}
            <Box p={6} borderBottom="1px solid" borderColor={useColorModeValue('gray.100', 'gray.700')}>
              <InputGroup size="lg">
                <InputLeftElement pointerEvents="none">
                  <FiSearch color="gray.400" size={20} />
                </InputLeftElement>
                <Input
                  ref={inputRef}
                  placeholder={t('search.placeholder')}
                  value={query}
                  onChange={(e) => handleSearch(e.target.value)}
                  onKeyDown={handleKeyDown}
                  bg="transparent"
                  border="none"
                  fontSize="lg"
                  _focus={{ boxShadow: 'none' }}
                  _placeholder={{ color: 'gray.400' }}
                />
                {query && (
                  <IconButton
                    aria-label="Clear search"
                    icon={<FiX />}
                    size="sm"
                    variant="ghost"
                    onClick={handleClear}
                    color="gray.400"
                    _hover={{ color: 'gray.600' }}
                  />
                )}
              </InputGroup>
            </Box>

            {/* Results */}
            <Box maxH="500px" overflow="auto">
              {renderResults()}
            </Box>

            {/* Footer with keyboard shortcuts */}
            <Box p={4} borderTop="1px solid" borderColor={useColorModeValue('gray.100', 'gray.700')} bg={useColorModeValue('gray.50', 'gray.700')}>
              <HStack spacing={4} fontSize="xs" color="gray.500" justify="center">
                <HStack spacing={1}>
                  <Text>↑↓</Text>
                  <Text>{t('search.navigate')}</Text>
                </HStack>
                <HStack spacing={1}>
                  <Text>↵</Text>
                  <Text>{t('search.select')}</Text>
                </HStack>
                <HStack spacing={1}>
                  <Text>⌘K</Text>
                  <Text>{t('search.search')}</Text>
                </HStack>
                <HStack spacing={1}>
                  <Text>ESC</Text>
                  <Text>{t('search.close')}</Text>
                </HStack>
              </HStack>
            </Box>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
});

GlobalSearch.displayName = 'GlobalSearch'; 